import type { components } from '@connected/api-client/api.generated';
import type { RadioGroupOption } from '@connected/form-fields-v2/components/form/radio-group';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@connected/ui/components/tooltip';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Edit, PlusIcon, ShieldQuestionIcon, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { Breadcrumb } from '@/components/breadcrumb';
import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/settings/scope12/$scopeName/',
)({
    component: RouteComponent,
    validateSearch: z.object({
        search: z.string().optional(),
    }),
});

type CheckListItems = components['schemas']['ListElectricalCheckListItemQueryResultItem'];

type CheckListSingleItem = components['schemas']['ListElectricalCheckListItemQueryResultItem'] & {
    ElectricalCheckListId: string | null;
    Description: string | null;
};

function ScopeModal(
    {
        isOpen,
        setIsOpen,
        item,
        activeTab,
        electricalCheckListId,
    }: {
        isOpen: boolean;
        setIsOpen: (isOpen: boolean) => void;
        item: CheckListItems | null;
        activeTab: RadioGroupOption<CheckListItems['Kind']> | null;
        electricalCheckListId: string | null;
    },
) {
    const { t } = useTranslation('dashboard');

    const [loading, setIsLoading] = useState<boolean>(false);

    const [isNVT, setNVT] = useState<boolean>(!item?.EnableNotApplicable);
    const createScopeItem = api.useMutation(
        'post',
        '/api/v2/electrical/checklists/items/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.checklists.listChecklist({ electricalCheckListId: electricalCheckListId ?? '' })).then(() => {
                    toast.success(t('dashboard.settings.checklist.message.createSuccess'));
                    setIsOpen(false);
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'CreateElectricalCheckListItemCommandResult',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/checklists/items/create' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
            onMutate: () => setIsLoading(true),
        },
    );
    const updateScopeItem = api.useMutation(
        'put',
        '/api/v2/electrical/checklists/items/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(portaalQueryOptions.checklists.listChecklist({ electricalCheckListId: electricalCheckListId ?? '' })).then(() => {
                    toast.success(t('dashboard.settings.checklist.message.editSuccess'));
                    setIsOpen(false);
                },
                );
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'EditElectricalCheckListItemCommandResult',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/checklists/items/edit' },
                );
            },
            onMutate: () => setIsLoading(true),
        },
    );

    const DEFAULT_VALUES: CheckListSingleItem = {
        ElectricalCheckListId: electricalCheckListId ?? '',
        ElectricalCheckListItemId: item?.ElectricalCheckListItemId,
        Description: item?.Description ?? null,
        Kind: item?.Kind ?? activeTab?.value,
        EnableNotApplicable: item?.EnableNotApplicable ?? false,
        EnableObservations: item?.EnableObservations ?? false,
        NegativeLabelText: item?.NegativeLabelText ?? '',
        NotApplicableLabelText: item?.NotApplicableLabelText ?? '',
        PositiveLabelText: item?.PositiveLabelText ?? '',
    };

    const form = useAppForm(
        {
            defaultValues: DEFAULT_VALUES,
            onSubmit: async (values) => {
                if (!item) {
                    await createScopeItem.mutateAsync({
                        body: {
                            Description: values.value.Description ?? '',
                            ElectricalCheckListId: electricalCheckListId ?? '',
                            Kind: activeTab?.value ?? 'Scope12InspectionPlanDocument',
                            EnableNotApplicable: values.value.EnableNotApplicable ?? false,
                            EnableObservations: values.value.EnableObservations ?? false,
                            PositiveLabelText: values.value.PositiveLabelText ?? '',
                            NegativeLabelText: values.value.NegativeLabelText ?? '',
                            NotApplicableLabelText: values.value.NotApplicableLabelText ?? '',
                        },
                    });
                }
                else {
                    await updateScopeItem.mutateAsync({
                        body: {
                            ElectricalCheckListItemId: item?.ElectricalCheckListItemId ?? '',
                            Description: values.value.Description ?? '',
                            EnableNotApplicable: values.value.EnableNotApplicable ?? false,
                            EnableObservations: values.value.EnableObservations ?? false,
                            PositiveLabelText: values.value.PositiveLabelText ?? '',
                            NegativeLabelText: values.value.NegativeLabelText ?? '',
                            NotApplicableLabelText: values.value.NotApplicableLabelText ?? '',
                            Order: item?.Order ?? 1,
                        },
                    });
                }
            },
        },
    );

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <div className="flex flex-col">
                        <PopoutTitle>
                            {!item ? (t('dashboard.settings.checklist.create.title')) : t('dashboard.settings.checklist.update.title')}
                        </PopoutTitle>
                    </div>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                        <form.AppField
                            name="Description"
                            validators={{
                                onChange: validators.createElectricalCheckListItemCommand.description,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.scope12.table.name')} />
                                    <field.Textarea placeholder={t('dashboard.settings.scope12.table.namePlaceholder')} />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <div className="flex items-center gap-2">
                            <form.AppField
                                name="EnableObservations"
                                validators={{
                                    onChange: validators.createElectricalCheckListItemCommand.enableObservations,
                                }}
                                children={field => (
                                    <field.Switch>
                                        <field.Label.Title label={t('dashboard.settings.scope12.table.name')} />
                                        <field.ValidationError />
                                    </field.Switch>
                                )}
                            />
                            <p className="inline text-sm">{t('dashboard.settings.scope12.table.enableObservations')}</p>
                        </div>

                        <div className="flex items-center gap-2" onClick={() => setNVT(!isNVT)}>
                            <form.AppField
                                name="EnableNotApplicable"
                                validators={{
                                    onChange: validators.createElectricalCheckListItemCommand.enableNotApplicable,
                                }}
                                children={field => (
                                    <field.Switch>
                                        <field.Label.Title label={t('dashboard.settings.scope12.table.name')} />
                                        <field.ValidationError />
                                    </field.Switch>
                                )}
                            />
                            <p className="inline text-sm">{t('dashboard.settings.scope12.table.enableNotApplicable')}</p>
                        </div>
                        <p className="w-full border-text-tertiary mt-1"></p>
                        <div>
                            <p className="text-sm flex">
                                {t('dashboard.settings.scope12.table.changeButtonNames')}
                                <Tooltip>
                                    <TooltipTrigger>
                                        <ShieldQuestionIcon className="mt-1 ml-1 cursor-pointer" size={16} />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs bg-text-secondary text-background-primary">
                                        <div className="flex flex-col items-start gap-2 p-2">
                                            <span className="text-sm">{t('dashboard.settings.scope12.tooltip.title')}</span>
                                            <span className="text-xs">{t('dashboard.settings.scope12.tooltip.description')}</span>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </p>
                            <p className="text-sm text-text-tertiary">
                                {t('dashboard.settings.scope12.table.changeButtonNamesDescription')}
                            </p>
                        </div>

                        <form.AppField
                            name="PositiveLabelText"
                            validators={{
                                onChange: validators.createElectricalCheckListItemCommand.positiveLabelText,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.scope12.table.positive')} />
                                    <field.Input placeholder={t('dashboard.settings.scope12.table.positiveDescription')} />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="NegativeLabelText"
                            validators={{
                                onChange: validators.createElectricalCheckListItemCommand.negativeLabelText,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.scope12.table.negative')} />
                                    <field.Input placeholder={t('dashboard.settings.scope12.table.negativeDescription')} />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        {
                            !isNVT && (
                                <form.AppField
                                    name="NotApplicableLabelText"
                                    validators={{
                                        onChange: validators.createElectricalCheckListItemCommand.notApplicableLabelText,
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.settings.scope12.table.notApplicable')} />
                                            <field.Input placeholder={t('dashboard.settings.scope12.table.notApplicableDescription')} />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                />
                            )
                        }

                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{t('dashboard.create')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

function RouteComponent() {
    const { organizationId, scopeName } = Route.useParams();
    const { t } = useTranslation('dashboard');
    const search = Route.useSearch();

    const [isOpenModal, setOpenModal] = useState<boolean>(false);
    const [item, setItem] = useState<CheckListItems | null>(null);

    const tabsKind = useMemo<RadioGroupOption<CheckListItems['Kind']>[]>(() => [
        {
            value: 'Scope12InspectionPlanDocument',
            label: t('dashboard.settings.scope2.tabs.scope12InspectionPlanDocument'),
        },
        {
            value: 'Scope12VisualInspection',
            label: t('dashboard.settings.scope2.tabs.scope12VisualInspection'),
        },
        {
            value: 'Scope12AdditionalInspectionPoints',
            label: t('dashboard.settings.scope2.tabs.scope12AdditionalInspectionPoints'),
        },
        {
            value: 'Scope12DocumentationDCSide',
            label: t('dashboard.settings.scope2.tabs.scope12DocumentationDCSide'),
        },
        {
            value: 'Scope12ManualAndDataSpecifications',
            label: t('dashboard.settings.scope2.tabs.scope12ManualAndDataSpecifications'),
        },
        {
            value: 'Scope12GeneralRemarks',
            label: t('dashboard.settings.scope2.tabs.scope12GeneralRemarks'),
        },
        {
            value: 'Scope12PVInstallationPowerLine',
            label: t('dashboard.settings.scope2.tabs.scope12PVInstallationPowerLine'),
        },
        {
            value: 'Scope12InspectionExecution',
            label: t('dashboard.settings.scope2.tabs.scope12InspectionExecution'),
        },
        {
            value: 'Scope12DocumentationACSide',
            label: t('dashboard.settings.scope2.tabs.scope12DocumentationACSide'),
        },
        {
            value: 'Scope12MeasurementsAndTesting',
            label: t('dashboard.settings.scope2.tabs.scope12MeasurementsAndTesting'),
        },
        {
            value: 'Scope12VisualInspectionOtherParts',
            label: t('dashboard.settings.scope2.tabs.scope12VisualInspectionOtherParts'),
        },
        {
            value: 'Scope12RoofConstructionCalculation',
            label: t('dashboard.settings.scope2.tabs.scope12RoofConstructionCalculation'),
        },
    ], [t]);

    useEffect(() => {
        if (!tabsKind.find(x => x.value?.toLocaleLowerCase() === scopeName.toLocaleLowerCase())) {
            throw redirect({
                to: '/dashboard/organizations/$organizationId/settings/scope12',
                params: {
                    organizationId,
                },
            });
        }
    }, [tabsKind, scopeName, organizationId]);

    const activeTab = tabsKind.find(x => x.value?.toLocaleLowerCase() === scopeName.toLocaleLowerCase()) ?? null;

    const getActiveChecklist = useQuery(
        elektraAppQueryOptions.organisation.management.checkListOptions({
            organisationId: organizationId,
        }),
    );

    const activeChecklist = useQuery(
        {
            ...portaalQueryOptions.checklists.listChecklist({
                electricalCheckListId: getActiveChecklist?.data?.CheckLists[0]?.ElectricalCheckListId ?? '',
            }),
            enabled: !!getActiveChecklist.data?.CheckLists[0]?.ElectricalCheckListId,
        },
    );

    const removeChecklistItem = api.useMutation('delete', '/api/v2/electrical/checklists/items/delete', {
        onSuccess: () => {
            queryClient.invalidateQueries(portaalQueryOptions.checklists.listChecklist({ electricalCheckListId: getActiveChecklist.data?.CheckLists[0]?.ElectricalCheckListId ?? '' })).then(() => {
                toast.success(t('dashboard.settings.checklist.message.removeSuccess'));
            },
            );
        },
        onError: (error) => {
            logger.error(
                {
                    system: 'DeleteElectricalCheckListItemCommandResult',
                    error,
                },
                { apiHttpMethod: 'Delete', apiUrl: '/api/v2/electrical/checklists/items/delete' },
            );
            toast.error(error?.Message ?? t('dashboard.general.messages.error'));
        },
    });

    const columns = useMemo(() => [
        {
            accessorKey: 'Naam',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: CheckListItems } }) => (
                <DataTable.Cell variant="medium">
                    <div className="max-w-screen-lg">
                        {row?.original?.Description}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }: { row: { original: CheckListItems } }) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={async () => {
                                if (await showConfirm(t('dashboard.settings.recommendations.message.confirmDelete'))) {
                                    await removeChecklistItem.mutateAsync({
                                        params: {
                                            query: {
                                                ElectricalCheckListItemId: row?.original?.ElectricalCheckListItemId ?? '',
                                            },
                                        },
                                    });
                                }
                            }}
                        >
                            <Trash2 className="text-text-primary/40 size-5" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit  h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                setOpenModal(true);
                                setItem(row?.original);
                            }}
                        >
                            <Edit className="text-text-primary/40 size-5" />
                        </Button>
                    </div>
                );
            },
        },
    ], [removeChecklistItem, t]);

    const tableColumns = useMemo(
        () => activeChecklist.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [activeChecklist.isLoading, columns],
    );

    const tableData = useMemo<CheckListItems[]>(
        () => {
            if (activeChecklist.isError) {
                toast.error(activeChecklist.error?.Message);
                return [];
            }
            const searchTerm = search.search?.toLowerCase() || '';

            return (
                activeChecklist.data?.Items
                    ?.filter((norm) => {
                        if (norm.Kind?.toLowerCase() !== scopeName.toLocaleLowerCase())
                            return false;
                        if (!searchTerm)
                            return true;

                        return [
                            norm.Description || '',
                        ].some(field => field?.toString().toLowerCase().includes(searchTerm));
                    })
                    .map(norm => norm)
                    || []
            );
        },
        [activeChecklist.isError, activeChecklist.data?.Items, activeChecklist.error?.Message, search.search, scopeName],
    );

    if (getActiveChecklist.isLoading || activeChecklist.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div>
            <Breadcrumb
                showHome={false}
                items={[
                    {
                        name: t('dashboard.settings.scope12.title'),
                        to: '/dashboard/organizations/$organizationId/settings/scope12',
                        params: { locationId: organizationId },
                    },
                    {
                        isActive: true,
                        name: t(activeTab && typeof activeTab.label === 'string' ? activeTab.label : ''),
                    },
                ]}
            />
            <div className="w-full">
                <DataTable.TableHead
                    title={activeTab && typeof activeTab.label === 'string' ? activeTab.label : ''}
                    subtitle={t('dashboard.settings.checklist.subtitle')}
                    search={Route}
                    buttons={[
                        {
                            name: t('dashboard.settings.scope12.item'),
                            icon: <PlusIcon size={19} />,
                            callback: () => {
                                setOpenModal(true);
                                setItem(null);
                            },
                        },
                    ]}
                />
            </div>
            <DataTable
                className="rounded-tl-none rounded-tr-none"
                columns={tableColumns}
                data={tableData}
                pageSize={10}
            />
            {
                isOpenModal && (
                    <ScopeModal
                        activeTab={activeTab}
                        isOpen={isOpenModal}
                        setIsOpen={setOpenModal}
                        electricalCheckListId={getActiveChecklist.data?.CheckLists[0]?.ElectricalCheckListId ?? ''}
                        item={item ?? null}
                    />
                )
            }
        </div>
    );
}
